import React, { useState } from "react";
import "../styles/Login.css";
import { useNavigate, Link } from "react-router-dom";
import ChangePasswordModal from "../components/common/ChangePasswordModal";
import MfaCodeModal from "../components/common/MfaCodeModal";
import MfaSetupModal from "../components/common/MfaSetupModal";
import { loginRequest } from "../utils/apiUtils";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { buildApiUrl, API_ENDPOINTS, APP_DISPLAY_NAME } from "../config/api";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [rememberMe, setRememberMe]     = useState(false);
  const [errors, setErrors]             = useState({ email: "", password: "" });
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendSent, setResendSent]     = useState(false);
  const [approvalPending, setApprovalPending]   = useState(false);
  const [approvalRejected, setApprovalRejected] = useState(false);
  const [mfaStep, setMfaStep]   = useState(null);   // null | "validate" | "setup"
  const [tempToken, setTempToken] = useState(null);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    let valid = true;
    let newErrors = { email: "", password: "" };

    if (email.trim() === "") {
      newErrors.email = "Por favor, ingresa tu correo electrónico.";
      valid = false;
    }
    if (password.trim() === "") {
      newErrors.password = "Por favor, ingresa tu contraseña.";
      valid = false;
    }
    setErrors(newErrors);
    setEmailNotVerified(false);
    setResendSent(false);
    setApprovalPending(false);
    setApprovalRejected(false);

    if (valid) {
      try {
        const datos = await loginRequest({ email, password });

        if (datos.requiresMfa) {
          setTempToken(datos.tempToken);
          setMfaStep("validate");
          return;
        }

        if (datos.requiresMfaSetup) {
          if (datos.usuarioLogueado?.type) {
            localStorage.setItem("userType", datos.usuarioLogueado.type);
          }
          setMfaStep("setup");
          return;
        }

        if (datos.usuarioLogueado?.type) {
          localStorage.setItem("userType", datos.usuarioLogueado.type);
        }

        if (datos.mustChangePassword) {
          setShowChangePasswordModal(true);
          return;
        }

        navigate("/survey");
      } catch (loginError) {
        if (loginError.message === "APPROVAL_PENDING") {
          setApprovalPending(true);
          setErrors({ email: "", password: "" });
        } else if (loginError.message === "APPROVAL_REJECTED") {
          setApprovalRejected(true);
          setErrors({ email: "", password: "" });
        } else if (loginError.message === "EMAIL_NOT_VERIFIED") {
          setEmailNotVerified(true);
          setErrors({ email: "", password: "" });
        } else {
          setErrors({
            email: "",
            password: loginError.message || "Credenciales incorrectas",
          });
        }
      }
    }
  };

  const handleResendVerification = async () => {
    if (!email.trim()) return;
    try {
      await fetch(buildApiUrl(API_ENDPOINTS.resendVerification), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setResendSent(true);
    } catch {
      // silent — el usuario puede volver a intentar
    }
  };

  const handleMfaSuccess = (data) => {
    if (data.usuarioLogueado?.type) {
      localStorage.setItem("userType", data.usuarioLogueado.type);
    }
    if (data.mustChangePassword) {
      setMfaStep(null);
      setShowChangePasswordModal(true);
      return;
    }
    navigate("/survey");
  };

  const handleMfaExpired = () => {
    setMfaStep(null);
    setTempToken(null);
    setErrors({
      email: "",
      password: "Tu sesión de verificación expiró. Por favor, inicia sesión de nuevo.",
    });
  };

  const handleMfaSetupSuccess = () => {
    navigate("/survey");
  };

  const handlePasswordChanged = () => {
    navigate("/survey");
  };

  const handleCloseModal = () => {
    setShowChangePasswordModal(false);
    setEmail("");
    setPassword("");
  };

  return (
    <div className="login-page">
      <div className="login-card">
        {mfaStep === "validate" ? (
          <MfaCodeModal
            tempToken={tempToken}
            onSuccess={handleMfaSuccess}
            onExpired={handleMfaExpired}
          />
        ) : (
          <>
            {/* Logo */}
            <div className="login-logo">
              <img src="/LOGOCETIUCLOGIN.png" alt={APP_DISPLAY_NAME} />
            </div>

            {/* Título */}
            <h2 className="login-title">Inicia sesión</h2>

            {/* Subtítulo */}
            <p className="login-subtitle">
              Evalúa la Satisfacción de los Usuarios de tu organización y define
              planes de mejora.
            </p>

            {/* Formulario */}
            <form onSubmit={handleSubmit} className="login-form">

              {/* Email */}
              <div className="login-field">
                <label htmlFor="login-email">Correo electrónico</label>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tunombre@mail.com"
                />
                {errors.email && (
                  <span className="login-error">{errors.email}</span>
                )}
              </div>

              {/* Contraseña */}
              <div className="login-field">
                <label htmlFor="login-password">Contraseña</label>
                <div className="login-password-wrapper">
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Escribe tu contraseña"
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowPassword((p) => !p)}
                    tabIndex={-1}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
                {errors.password && (
                  <span className="login-error">{errors.password}</span>
                )}
              </div>

              {/* Recordarme + ¿Olvidaste? */}
              <div className="login-options-row">
                <label className="login-remember">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  Recordarme
                </label>
                <button
                  type="button"
                  className="login-forgot-btn"
                  onClick={() => navigate("/forgot-password")}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              {/* Submit */}
              <button type="submit" className="login-submit-btn">
                Iniciar sesión
              </button>
            </form>

            {approvalPending && (
              <div className="login-verify-banner">
                <p>Tu cuenta está pendiente de aprobación por un administrador. Te notificaremos por correo cuando esté lista.</p>
              </div>
            )}

            {approvalRejected && (
              <div className="login-verify-banner login-rejected-banner">
                <p>Tu solicitud de registro fue rechazada. Contacta al administrador para más información.</p>
              </div>
            )}

            {emailNotVerified && (
              <div className="login-verify-banner">
                <p>Debes verificar tu correo electrónico antes de iniciar sesión.</p>
                {resendSent ? (
                  <span className="login-verify-sent">
                    Enlace enviado. Revisa tu bandeja de entrada.
                  </span>
                ) : (
                  <button
                    type="button"
                    className="login-secondary-link"
                    onClick={handleResendVerification}
                  >
                    Reenviar correo de verificación
                  </button>
                )}
              </div>
            )}

            <div className="login-links-row">
              <Link to="/register" className="login-secondary-link">
                ¿No tienes cuenta? <strong>Regístrate</strong>
              </Link>
              <a
                href="https://home.cetiuc.com/privacidad"
                target="_blank"
                rel="noopener noreferrer"
                className="login-secondary-link"
              >
                Privacidad de Datos
              </a>
            </div>
          </>
        )}
      </div>

      {mfaStep === "setup" && (
        <MfaSetupModal onSuccess={handleMfaSetupSuccess} />
      )}

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={handleCloseModal}
        onPasswordChanged={handlePasswordChanged}
      />
    </div>
  );
};

export default Login;
